import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { isNativePlatform } from '@lib/platform';
import { lightImpact } from '@utils/haptics';
import { logError } from '@utils/logger';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<unknown>;
  threshold?: number;
  maxPullDistance?: number;
  resistance?: number;
  disabled?: boolean;
  /** Returns the current scroll position. Defaults to `() => window.scrollY` (document-level scroll). */
  getScrollTop?: () => number;
}

interface UsePullToRefreshReturn {
  pullDistance: number;
  isRefreshing: boolean;
  isPastThreshold: boolean;
  maxPullDistance: number;
  bindContainer: {
    ref: (node: HTMLElement | null) => void;
  };
}

export const REFRESHING_HEIGHT = 48;

const defaultGetScrollTop = () => window.scrollY;

export function usePullToRefresh({
  onRefresh,
  threshold = 60,
  maxPullDistance = 80,
  resistance = 2.5,
  disabled = false,
  getScrollTop = defaultGetScrollTop,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Container element as state so setting it triggers a re-render and re-runs the effect
  const [container, setContainer] = useState<HTMLElement | null>(null);

  // Refs for gesture tracking (no re-renders)
  const onRefreshRef = useRef(onRefresh);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const isTrackingRef = useRef(false);
  const isFirstMoveRef = useRef(true);
  const hasTriggeredHapticRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const disabledRef = useRef(disabled);
  const thresholdRef = useRef(threshold);
  const maxPullDistanceRef = useRef(maxPullDistance);
  const resistanceRef = useRef(resistance);
  const getScrollTopRef = useRef(getScrollTop);

  // Sync latest values into refs during render
  onRefreshRef.current = onRefresh;
  disabledRef.current = disabled;
  thresholdRef.current = threshold;
  maxPullDistanceRef.current = maxPullDistance;
  resistanceRef.current = resistance;
  getScrollTopRef.current = getScrollTop;

  const handleRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    setPullDistance(REFRESHING_HEIGHT);

    try {
      await onRefreshRef.current();
    } catch (error) {
      // onRefresh errors are handled by the caller (e.g. TanStack Query).
      // We catch here to prevent unhandled promise rejections from the
      // fire-and-forget call in handleTouchEnd.
      logError('[usePullToRefresh] onRefresh failed:', error);
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, []);

  // Ref callback for the container element
  const refCallback = useCallback((node: HTMLElement | null) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    if (!container) return;
    // Skip touch-event PTR on Capacitor — native WebViews have their own
    // scroll gesture recognizers that conflict with passive:false + preventDefault
    if (isNativePlatform()) return;

    // Small tolerance for sub-pixel scroll positions and scroll restoration
    const SCROLL_TOP_TOLERANCE = 2;

    function handleTouchStart(e: TouchEvent) {
      if (disabledRef.current || isRefreshingRef.current) return;
      if (getScrollTopRef.current() > SCROLL_TOP_TOLERANCE) return;

      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      isTrackingRef.current = true;
      isFirstMoveRef.current = true;
      hasTriggeredHapticRef.current = false;
    }

    function handleTouchMove(e: TouchEvent) {
      if (!isTrackingRef.current) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - startYRef.current;
      const deltaX = touch.clientX - startXRef.current;

      // On first move: reject horizontal swipes
      if (isFirstMoveRef.current) {
        isFirstMoveRef.current = false;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          isTrackingRef.current = false;
          return;
        }
      }

      // Only track downward pulls
      if (deltaY <= 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      // If page has scrolled down since touchstart, abort
      if (getScrollTopRef.current() > SCROLL_TOP_TOLERANCE) {
        isTrackingRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      if (e.cancelable) e.preventDefault();

      const pullDelta = Math.min(deltaY / resistanceRef.current, maxPullDistanceRef.current);
      pullDistanceRef.current = pullDelta;
      setPullDistance(pullDelta);

      // Haptic at threshold (once per gesture)
      if (pullDelta >= thresholdRef.current && !hasTriggeredHapticRef.current) {
        hasTriggeredHapticRef.current = true;
        void lightImpact();
      }
    }

    function handleTouchEnd() {
      if (!isTrackingRef.current) return;
      isTrackingRef.current = false;

      if (pullDistanceRef.current >= thresholdRef.current) {
        void handleRefresh();
      } else {
        setPullDistance(0);
      }
      pullDistanceRef.current = 0;
    }

    // Use passive: false on touchmove to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [container, handleRefresh]);

  const isPastThreshold = pullDistance >= threshold;

  const bindContainer = useMemo(() => ({ ref: refCallback }), [refCallback]);

  return {
    pullDistance,
    isRefreshing,
    isPastThreshold,
    maxPullDistance,
    bindContainer,
  };
}
